import { View, Text, styled } from 'tamagui'
import { ArrowLeft, Settings, Hash, Shield, Users, Mail, Lock, CreditCard, Smile, FileText, Webhook } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import type { ServerSettingsTab } from '@/store'
import { TierBadge } from '../../common'

const Nav = styled(View, {
  width: 220,
  backgroundColor: '$sidebar',
  borderRightWidth: 1,
  borderRightColor: '$borderDefault',
  flexDirection: 'column',
  flexShrink: 0,
})

const NavHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$surfaceRaised',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const HeaderText = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 14,
  color: '$sidebarMuted',
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 12,
  paddingTop: 16,
  paddingBottom: 4,
})

const NavItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 32,
  paddingHorizontal: 12,
  marginHorizontal: 8,
  borderRadius: '$borderRadiusMd',
  gap: 8,
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
  variants: {
    active: {
      true: {
        backgroundColor: '$sidebarActive',
      },
    },
  } as const,
})

const NavLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
})

const tabs: { id: ServerSettingsTab; label: string; icon: typeof Settings }[] = [
  { id: 'overview', label: 'Overview', icon: Settings },
  { id: 'channels', label: 'Channels', icon: Hash },
  { id: 'roles', label: 'Roles', icon: Shield },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'invites', label: 'Invites', icon: Mail },
  { id: 'emoji', label: 'Emoji', icon: Smile },
  { id: 'audit-log', label: 'Audit Log', icon: FileText },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'safety', label: 'Safety', icon: Lock },
  { id: 'billing', label: 'Billing', icon: CreditCard },
]

export function ServerSettingsNav() {
  const { activeServerId, serverSettingsTab, setServerSettingsTab, navigateToServer, setActiveChannel } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)

  const handleBack = () => {
    if (!activeServerId) return
    navigateToServer(activeServerId)
    if (server) {
      const firstText = server.channels.find(c => c.type === 'text')
      if (firstText) setActiveChannel(firstText.id)
    }
  }

  return (
    <Nav>
      <NavHeader onPress={handleBack}>
        <ArrowLeft size={16} color="$sidebarMuted" />
        <HeaderText>Back{server ? ` to ${server.name}` : ''}</HeaderText>
      </NavHeader>

      <SectionLabel>Settings</SectionLabel>
      {tabs.map((tab) => {
        const Icon = tab.icon
        const isBilling = tab.id === 'billing'
        return (
          <NavItem
            key={tab.id}
            active={serverSettingsTab === tab.id}
            onPress={() => setServerSettingsTab(tab.id)}
          >
            <Icon size={16} color={serverSettingsTab === tab.id ? '$accent' : '$sidebarMuted'} />
            <NavLabel color={serverSettingsTab === tab.id ? '$surfaceForeground' : '$sidebarMuted'}>
              {tab.label}
            </NavLabel>
            {isBilling && server && (
              <View marginLeft="auto">
                <TierBadge tier={server.tier} size="sm" />
              </View>
            )}
          </NavItem>
        )
      })}
    </Nav>
  )
}
